import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CONTACT_INFO, MENU_STRUCTURE } from "../data";

export default function Footer() {
  const services = MENU_STRUCTURE.find(item => item.label === "Jeg tilbyder")?.children || [];

  useEffect(() => {
    // Dynamically load the Iubenda script for the cookie policy widget
    if (!document.querySelector('script[src="https://cdn.iubenda.com/iubenda.js"]')) {
      const s = document.createElement("script");
      s.type = "text/javascript";
      s.src = "https://cdn.iubenda.com/iubenda.js";
      s.async = true;
      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(s, firstScript);
      } else {
        document.head.appendChild(s);
      }
    }
  }, []);

  return (
    <footer className="border-t border-brand-brown/10 bg-brand-beige-dark/50 text-brand-graphite">
      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 md:grid-cols-4">
          {/* Column 1: Brand & Contact Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://assets.auxodev.dk/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/logobilledepng.png"
                alt="Bachmann Tømrer Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-xs text-brand-graphite/70 leading-relaxed">
              Professionelt tømrerarbejde med fokus på kvalitet, solidt håndværk og tæt dialog. Vi hjælper private og erhverv i Silkeborg, Ry, Kjellerup og omegn.
            </p>
            <div className="space-y-2 pt-2 text-xs text-brand-graphite/80">
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-location-dot text-brand-brown w-4" />
                <a href={CONTACT_INFO.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-brand-brown hover:underline">
                  {CONTACT_INFO.address}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-phone text-brand-brown w-4" />
                <a href={`tel:${CONTACT_INFO.phoneRaw}`} className="hover:text-brand-brown hover:underline whitespace-nowrap">
                  {CONTACT_INFO.phone}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-brand-brown w-4" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-brand-brown hover:underline">
                  {CONTACT_INFO.email}
                </a>
              </p>
              <p className="flex items-center gap-2 pt-1 font-semibold">
                <span className="text-brand-brown">CVR nr.:</span> {CONTACT_INFO.cvr}
              </p>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-4">
            <h3 className="font-serif text-sm font-bold uppercase tracking-wider text-brand-graphite">
              Hurtige links
            </h3>
            <ul className="space-y-2.5 text-xs">
              <li>
                <Link to="/" className="text-brand-graphite/80 hover:text-brand-brown transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <Link to="/galleri" className="text-brand-graphite/80 hover:text-brand-brown transition-colors">
                  Galleri
                </Link>
              </li>
              <li>
                <Link to="/referencer-2" className="text-brand-graphite/80 hover:text-brand-brown transition-colors">
                  Referencer
                </Link>
              </li>
              <li>
                <Link to="/profil" className="text-brand-graphite/80 hover:text-brand-brown transition-colors">
                  Profil
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-brand-graphite/80 hover:text-brand-brown transition-colors">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Services */}
          <div className="space-y-4">
            <h3 className="font-serif text-sm font-bold uppercase tracking-wider text-brand-graphite">
              Vores ydelser
            </h3>
            <ul className="space-y-2.5 text-xs">
              {services.slice(0, 6).map((service, idx) => (
                <li key={idx}>
                  <Link to={service.href} className="text-brand-graphite/80 hover:text-brand-brown transition-colors">
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact CTA */}
          <div className="space-y-4">
            <h3 className="font-serif text-sm font-bold uppercase tracking-wider text-brand-graphite">
              Skal vi hjælpe dig?
            </h3>
            <p className="text-xs text-brand-graphite/70 leading-relaxed">
              Kontakt os i dag for en uforpligtende samtale eller et skræddersyet tilbud på dit næste projekt.
            </p>
            <div className="space-y-3 pt-2">
              <Link
                to="/kontakt"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-brown py-3 text-center text-xs font-semibold uppercase tracking-wider text-white shadow-md transition-all duration-300 hover:bg-brand-brown-dark hover:shadow-lg"
              >
                <i className="fa-solid fa-paper-plane" /> Få et tilbud
              </Link>
              <a
                href={`tel:${CONTACT_INFO.phoneRaw}`}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-brand-brown/30 bg-white py-3 text-center text-xs font-semibold uppercase tracking-wider text-brand-brown shadow-sm transition-all duration-300 hover:bg-brand-beige"
              >
                <i className="fa-solid fa-phone" /> Ring nu
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Copyright Bar */}
      <div className="border-t border-brand-brown/10 bg-brand-beige-dark/80 py-6 text-center text-xs text-brand-graphite/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p>© {new Date().getFullYear()} Bachmann Tømrer. Alle rettigheder forbeholdes.</p>
            <span className="hidden sm:inline text-brand-graphite/30">|</span>
            <a href="https://www.iubenda.com/privacy-policy/30158731" className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-brand-brown transition-colors underline decoration-brand-brown/30" title="Privatlivspolitik ">Privatlivspolitik</a>
            <span className="hidden sm:inline text-brand-graphite/30">|</span>
            <a href="https://www.iubenda.com/privacy-policy/30158731/cookie-policy" className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-brand-brown transition-colors underline decoration-brand-brown/30" title="Cookiepolitik ">Cookiepolitik</a>
          </div>
          <p className="font-serif italic">Professionelt tømrerarbejde i Silkeborg, Ry og Kjellerup</p>
        </div>
      </div>
    </footer>
  );
}
